import { ChevronDown } from "lucide-react";
import {
  DataTable,
  type Column,
  fmt,
  FilterBlock,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Hyperlink,
  type SortingState,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { EMPTY_CELL, fromTimestamp, reformatPhoneNumber } from "@racoon/lib";
import { InviteDialog } from "~/features/invite-company/ui/invite-company.dialog";
import { Namespace, useT } from "@racoon/i18n";
import { IcWhatsapp } from "@racoon/icons";
import { useAccumulatedList } from "~/shared/hooks/use-accumulated-list";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { useCustomerOnboardingModal } from "~/features/customer-onboarding/hooks/use-customer-onboarding-modal";
import {
  CUSTOMERS_PAGE_SIZE,
  toCounterpartySearch,
  useCustomersFilters,
} from "../model/useCustomersFilters";
import { CUSTOMER_SORT_FIELDS } from "../model/customer-sort";
import { useCounterpartyContracts } from "../model/use-counterparty-contracts";
import { useOpenContract } from "~/features/customer-card/hooks/use-open-contract";
import { contractFromLabel } from "~/features/pickup-location-modal/model/contract-label";
import { toListSort } from "~/shared/lib/list-sort";
import {
  orgGrpcApi,
  useListCounterpartiesQuery,
} from "~/shared/api/organization/organization.grpc";
import { useMemo, useState } from "react";
import React from "react";

import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

const selectCounterpartyRows = (data: ConnectProto.CounterpartyList) => data.counterparties;
const selectListTotal = (data: ConnectProto.CounterpartyList) => data.listInfo?.totalItems;
const selectCounterpartyKey = (c: ConnectProto.Counterparty) => String(c.organization?.id ?? "");

export default function CustomersPage() {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "customers",
    selectedCompanyId || undefined,
  );
  const {
    contractStatus,
    customerTypeId,
    categories,
    selectedCategory,
    onCategoryChange,
    states,
    selectedState,
    onStateChange,
    searchValue,
    searchQuery,
    onSearchChange,
  } = useCustomersFilters(selectedCompanyId);

  const [inviteOpen, setInviteOpen] = useState(false);
  // Bumped after a card save: resets the accumulator, and the mutation's tag
  // invalidation makes page 0 hit the network rather than the cache.
  const [refreshToken, setRefreshToken] = useState(0);

  // The column sort, applied by the server to the whole match: the list loads a page at
  // a time, so sorting the loaded rows would order only those.
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = useMemo(() => toListSort(sorting, CUSTOMER_SORT_FIELDS), [sorting]);

  const search = toCounterpartySearch(searchQuery);

  const listArg = useMemo(
    () => ({
      organizationId: BigInt(selectedCompanyId ?? 0),
      ...(contractStatus !== undefined ? { status: contractStatus } : {}),
      ...(search ? { search } : {}),
      ...(customerTypeId !== undefined ? { customerTypeIds: [customerTypeId] } : {}),
      ...(sort && { listFilter: sort }),
    }),
    [selectedCompanyId, contractStatus, search, customerTypeId, sort],
  );
  const filterKey = [
    selectedCompanyId ?? "",
    contractStatus ?? "",
    search,
    customerTypeId ?? "",
    refreshToken,
    sort?.sortBy ?? "",
    sort?.sortDirection ?? "",
  ].join("|");

  const { rows, totalItems, isFetching, hasMore, loadNextPage } = useAccumulatedList<
    ConnectProto.CounterpartyList,
    ConnectProto.Counterparty
  >({
    useListQuery: useListCounterpartiesQuery,
    listEndpoint: orgGrpcApi.endpoints.listCounterparties,
    arg: listArg,
    pageSize: CUSTOMERS_PAGE_SIZE,
    filterKey,
    selectRows: selectCounterpartyRows,
    selectTotal: selectListTotal,
    selectRowKey: selectCounterpartyKey,
    skip: !selectedCompanyId,
  });

  const organizations = useMemo<ConnectProto.Organization[]>(
    () => rows.flatMap((c) => (c.organization ? [c.organization] : [])),
    [rows],
  );

  // «Договор»: the loaded rows' contracts, a request per page of rows.
  const counterpartyIds = useMemo(() => organizations.map((org) => org.id), [organizations]);
  const contractByCounterparty = useCounterpartyContracts(
    selectedCompanyId,
    counterpartyIds,
    CUSTOMERS_PAGE_SIZE,
  );
  const openContract = useOpenContract();

  // The invite dialog picks from every counterparty, not the loaded page of the current
  // status/search slice — its own unpaginated fetch, deferred until the dialog opens.
  const { data: inviteData } = useListCounterpartiesQuery(
    { organizationId: BigInt(selectedCompanyId ?? 0) },
    { skip: !selectedCompanyId || !inviteOpen },
  );
  const inviteOrganizations = useMemo<ConnectProto.Organization[]>(
    () =>
      (inviteData?.counterparties ?? []).flatMap((c) => (c.organization ? [c.organization] : [])),
    [inviteData],
  );

  const { openNavigation, openNew } = useCustomerCardModal({
    providerId: selectedCompanyId ?? undefined,
    onSaved: () => setRefreshToken((token) => token + 1),
  });

  // The card opens with prev/next over the loaded page.
  const handleRowClick = React.useCallback(
    (counterparty: ConnectProto.Counterparty) => {
      const rowIndex = organizations.findIndex((org) => org.id === counterparty.organization?.id);
      if (rowIndex < 0) return;
      openNavigation(organizations, rowIndex);
    },
    [organizations, openNavigation],
  );

  const { open: openOnboarding } = useCustomerOnboardingModal({
    providerId: selectedCompanyId ?? undefined,
    onFinished: () => setRefreshToken((token) => token + 1),
  });

  const handleAddClick = () => {
    openNew();
  };

  const handleOnboardingClick = () => {
    openOnboarding();
  };

  const columns = useMemo<Column<ConnectProto.Counterparty>[]>(() => {
    const all: Column<ConnectProto.Counterparty>[] = [
      {
        id: "index",
        header: t(Namespace.Customers, "table.index"),
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "name",
        header: t(Namespace.Customers, "table.name"),
        accessor: (c) => c.organization?.name ?? "",
        width: 420,
        cell: (info) => (
          <Hyperlink onOpen={() => handleRowClick(info.row.original)}>
            {((info.getValue() as string) ?? "").trim() || t(Namespace.Common, "unnamed")}
          </Hyperlink>
        ),
      },
      {
        id: "bin",
        header: t(Namespace.Customers, "table.bin"),
        accessor: (c) => c.organization?.bin?.trim() || EMPTY_CELL,
        width: 160,
      },
      {
        id: "type",
        header: t(Namespace.Customers, "table.type"),
        width: 220,
        cell: () => t(Namespace.Customers, "type.serviceReceiver"),
      },
      {
        id: "status",
        header: t(Namespace.Customers, "table.status"),
        width: 160,
        accessor: (c) => c.contractStatus === ConnectProto.ContractStatus.ACTIVE,
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 text-xs px-2 py-1">
              {t(Namespace.Customers, "status.active")}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-200 text-slate-700 text-xs px-2 py-1">
              {t(Namespace.Customers, "status.inactive")}
            </span>
          ),
      },
      {
        id: "contract",
        header: t(Namespace.Customers, "table.contract"),
        width: 220,
        accessor: (c) =>
          contractFromLabel(contractByCounterparty.get(String(c.organization?.id)), t) ||
          EMPTY_CELL,
        // As in the pickup-locations cascade: the contract opens its card.
        cell: (info) => {
          const contract = contractByCounterparty.get(String(info.row.original.organization?.id));
          if (!contract) return EMPTY_CELL;
          return (
            <Hyperlink className="truncate" onOpen={() => openContract(contract)}>
              {info.getValue() as string}
            </Hyperlink>
          );
        },
      },
      {
        id: "created_at",
        header: t(Namespace.Customers, "table.createdAt"),
        accessor: (c) => fromTimestamp(c.organization?.createdAt),
        width: 220,
        cell: (info) => {
          const d = info.getValue() as Date | null;
          return d ? fmt.dateTime(d) : "-";
        },
      },
      {
        id: "phone_number",
        header: t(Namespace.Customers, "table.phone_number"),
        accessor: (c) => c.organization?.primaryContact?.phone || "-",
        cell: (info) => {
          const phone = info.getValue() as string;
          if (phone === "-") return phone;
          // Same affordance as the sites registry: the number hands the
          // conversation to WhatsApp rather than opening a card.
          return (
            <span className="inline-flex items-center gap-1.5">
              <IcWhatsapp className="h-4 w-4 shrink-0 text-green-500" />
              <Hyperlink href={`https://wa.me/${reformatPhoneNumber(phone)}`}>{phone}</Hyperlink>
            </span>
          );
        },
      },
      {
        id: "contact_name",
        header: t(Namespace.Customers, "table.contact_name"),
        accessor: (c) => c.organization?.primaryContact?.name || "-",
      },
    ];
    return all.map((column) => ({ ...column, sortable: column.id in CUSTOMER_SORT_FIELDS }));
  }, [t, handleRowClick, contractByCounterparty, openContract]);

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1 gap-4">
      <FilterBlock
        states={states}
        selectedState={selectedState}
        onStateChange={onStateChange}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
        statesWithCategories
        onSearchChange={onSearchChange}
        searchValue={searchValue}
        searchPlaceholder={t(Namespace.Customers, "searchNameOrBin")}
        totalCount={totalItems}
        itemType="customers"
        additionalButtons={
          <>
            <Button
              id="customers-invite-button"
              size={"sm"}
              variant="opacityBlueWithOutline"
              onClick={() => setInviteOpen((prev) => !prev)}
              className="!px-2 md:px-4 flex-auto md:flex-initial"
            >
              <span className="block md:hidden">{t(Namespace.Customers, "inviteTitleShort")}</span>
              <span className="hidden md:block">{t(Namespace.Customers, "inviteTitle")}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  id="customers-add-button"
                  size={"sm"}
                  variant="opacityBlueWithOutline"
                  className="!px-2 md:px-4 flex-auto md:flex-initial"
                >
                  <span className="block md:hidden">{t(Namespace.Buttons, "add")}</span>
                  <span className="hidden md:block">{t(Namespace.Buttons, "addCustomer")}</span>
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleAddClick}>
                  {t(Namespace.Customers, "onboarding.menu.card")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleOnboardingClick}>
                  {t(Namespace.Customers, "onboarding.menu.wizard")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        additionalButtonsPosition="bottom"
      />

      <DataTable<ConnectProto.Counterparty>
        columns={columns}
        data={rows}
        loading={isFetching && rows.length === 0}
        onEndReached={hasMore ? loadNextPage : undefined}
        loadingMore={isFetching && rows.length > 0}
        rowKey={(c, i) => (c.organization ? String(c.organization.id) : `row-${i}`)}
        manualSorting
        onSortChange={setSorting}
        selectable={false}
        zebra
        className="min-w-[100%] flex-1 min-h-0"
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Customers}
      />

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        organizations={inviteOrganizations}
      />
    </div>
  );
}
